import { ChevronDown, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SeasonPicker } from '@/components/SeasonPicker';
import { SeasonBadge } from '@/components/SeasonBadge';
import { SeasonEditUnlockToggle } from '@/components/SeasonEditUnlockToggle';
import { FarmSwitcher } from '@/components/scope/FarmSwitcher';
import { ClientContextIndicator } from '@/components/consultant/ClientContextIndicator';
import type { ClientWindow } from '@/lib/consultant/types';
import { cn } from '@/lib/utils';

export interface TopBarMembership {
  tenant: { id: string; name: string };
}

interface TopBarProps {
  isMobile: boolean;
  onOpenMobileSidebar: () => void;
  companyLogo: string | null;
  shortTenantName: string;
  currentTenantName: string;
  tenants: TopBarMembership[];
  onSwitchTenant?: (tenantId: string) => void;
  /**
   * Открытое окно клиента (консультант работает в чужих данных). Стоит
   * ПРАВЕЕ названия своей компании, потому что читается как уточнение к нему:
   * «я — Кристалл, сейчас в данных клиента Иванов».
   */
  clientWindow?: ClientWindow | null;
  /**
   * Название СВОЕГО кабинета — подпись кнопки возврата из окна клиента
   * (AK-664). Без него кнопка говорила «вернуться к себе», а человек с двумя
   * кабинетами не понимал, в какой именно его вернут.
   */
  ownTenantName?: string | null;
  /**
   * Имя компании, чьи хозяйства в переключателе (внутри окна клиента — имя
   * клиента). Нужно только мобильной шапке: подпись строки «Холдинг».
   */
  scopeCompanyName?: string | null;
}

/**
 * TopBar — header под Layout (height ≈ 56px).
 *
 * Layout:
 *   left  → [burger? logo-square 32×32, tenant-name (Dropdown if multi-tenant),
 *             «Клиент: X ▾» если открыто окно клиента]
 *   right → десктоп: [SeasonPicker, SeasonBadge, EditUnlockToggle, пилюля
 *             области «хозяйство · га ▾»]; мобайл: [FarmSwitcher header]
 *
 * Один контрол выбора хозяйства на экран (AK-974, часть 2; approve founder
 * 20.10, комменты 18132/18140/18141):
 *   · десктоп ≥768 — пилюля правее «Сезон ▾», как на проде v1.2010
 *     (c184ceb07e, `DashboardScopeSelector`); в сайдбаре переключателя нет;
 *   · мобайл <768 — `FarmSwitcher variant="header"`, состояние main по В-3
 *     (#1101): на проде справа было пусто, founder это не отменял.
 * Оба варианта — один `FarmSwitcher` на одном источнике (`/scope/available`
 * + `useScopeSetter`), второго стора области нет.
 *
 * Mobile (<md): season widgets скрыты — переезжают в drawer (TODO).
 */
export function TopBar({
  isMobile,
  onOpenMobileSidebar,
  companyLogo,
  shortTenantName,
  currentTenantName,
  tenants,
  onSwitchTenant,
  clientWindow = null,
  ownTenantName = null,
  scopeCompanyName = null,
}: TopBarProps) {
  const initials = shortTenantName.slice(0, 2).toUpperCase();
  const multiTenant = tenants.length > 1;
  // Внутри окна клиента слева стоит своё имя, а сразу за ним — имя клиента.
  // Подпись снимает вопрос «а это чьё?» с наведения, не добавляя ни пикселя.
  const tenantTitle = clientWindow
    ? `Ваш кабинет — ${currentTenantName}`
    : currentTenantName;

  return (
    <header
      className={cn(
        'flex h-14 shrink-0 items-center justify-between border-b border-border bg-card',
        isMobile ? 'px-3 gap-2' : 'px-6 gap-4',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenMobileSidebar}
            aria-label="Открыть меню"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/5 border border-primary/15 text-primary text-sm font-bold overflow-hidden"
          title={
            companyLogo
              ? currentTenantName
              : 'Логотип компании (загрузить в Настройки → Компания)'
          }
        >
          {companyLogo ? (
            <img
              src={companyLogo}
              alt="logo"
              className="w-full h-full object-contain"
            />
          ) : (
            initials
          )}
        </div>
        {multiTenant ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity"
              >
                <span
                  className={cn(
                    'truncate text-sm font-semibold text-foreground',
                    isMobile ? 'max-w-[120px]' : 'max-w-[200px]',
                  )}
                  title={tenantTitle}
                >
                  {shortTenantName}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {tenants.map((m) => (
                <DropdownMenuItem
                  key={m.tenant.id}
                  onSelect={() => onSwitchTenant?.(m.tenant.id)}
                >
                  {m.tenant.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span
            className={cn(
              'truncate text-sm font-semibold text-foreground',
              isMobile ? 'max-w-[120px]' : 'max-w-[260px]',
            )}
            title={tenantTitle}
          >
            {shortTenantName}
          </span>
        )}
        {clientWindow && (
          <ClientContextIndicator
            window={clientWindow}
            ownTenantName={ownTenantName}
            compact={isMobile}
          />
        )}
      </div>

      <div
        className={cn(
          // min-w-0: на 390px переключатель хозяйства ужимается (имя в
          // многоточие), а не выталкивает шапку в горизонтальный скролл.
          'flex min-w-0 items-center',
          isMobile ? 'gap-1' : 'gap-3',
        )}
      >
        {/* Колокольчик уведомлений убран до появления самих уведомлений:
            кнопка без обработчика висела на каждом экране и ничего не делала
            (design review 18.08 А3; то же правило, что AK-294 к «мёртвой»
            навигации — мёртвый контрол хуже отсутствующего). Вернуть вместе с
            фичей, а не раньше. */}
        {isMobile ? (
          <FarmSwitcher
            variant="header"
            companyName={scopeCompanyName}
            className="min-w-0"
          />
        ) : (
          <>
            <SeasonPicker />
            <SeasonBadge />
            <SeasonEditUnlockToggle />
            <FarmSwitcher
              variant="pill"
              companyName={scopeCompanyName}
              className="min-w-0"
            />
          </>
        )}
      </div>
    </header>
  );
}

export default TopBar;
